/** Built-in member roles (UI-only for now; not sent in API payload). */
export const CATEGORY_MEMBER_ROLES = {
  DRIVER: "driver",
  NAVIGATOR: "navigator",
  MECHANIC: "mechanic",
};

export const SELECTABLE_MEMBER_ROLE_OPTIONS = [
  { value: CATEGORY_MEMBER_ROLES.NAVIGATOR, label: "Navigator" },
  { value: CATEGORY_MEMBER_ROLES.MECHANIC, label: "Mechanic" },
];

const PRESET_SELECTABLE_ROLES = [
  CATEGORY_MEMBER_ROLES.NAVIGATOR,
  CATEGORY_MEMBER_ROLES.MECHANIC,
];

/**
 * Resize / normalize member_roles for a given max_members count.
 * Index 0 is always driver. For count === 3, fills the last remaining
 * selectable role when exactly one of seats 2–3 is chosen.
 */
export const buildMemberRoles = (maxMembers, previousRoles = []) => {
  const count = Math.max(1, Math.floor(Number(maxMembers)) || 1);
  const prev = Array.isArray(previousRoles) ? previousRoles : [];
  const roles = Array.from({ length: count }, (_, index) => {
    if (index === 0) return CATEGORY_MEMBER_ROLES.DRIVER;
    return typeof prev[index] === "string" ? prev[index] : "";
  });

  if (count === 3) {
    const second = roles[1];
    const third = roles[2];
    if (
      PRESET_SELECTABLE_ROLES.includes(second) &&
      !PRESET_SELECTABLE_ROLES.includes(third)
    ) {
      roles[2] = PRESET_SELECTABLE_ROLES.find((role) => role !== second) || "";
    } else if (
      PRESET_SELECTABLE_ROLES.includes(third) &&
      !PRESET_SELECTABLE_ROLES.includes(second)
    ) {
      roles[1] = PRESET_SELECTABLE_ROLES.find((role) => role !== third) || "";
    } else if (
      PRESET_SELECTABLE_ROLES.includes(second) &&
      second === third
    ) {
      roles[2] = PRESET_SELECTABLE_ROLES.find((role) => role !== second) || "";
    }
  }

  return roles;
};

/** Update one seat's role; for 3 members, auto-assigns the remaining preset role. */
export const setMemberRoleAt = (roles, index, value, maxMembers) => {
  if (index === 0) return buildMemberRoles(maxMembers, roles);

  const count = Math.max(1, Math.floor(Number(maxMembers)) || 1);
  const next = buildMemberRoles(count, roles);
  next[index] = value;

  if (
    count === 3 &&
    (index === 1 || index === 2) &&
    PRESET_SELECTABLE_ROLES.includes(value)
  ) {
    const otherIndex = index === 1 ? 2 : 1;
    next[otherIndex] =
      PRESET_SELECTABLE_ROLES.find((role) => role !== value) || "";
  }

  return next;
};

export const initialCategoryForm = {
  title: "",
  key: "",
  description: "",
  max_members: "1",
  member_roles: [CATEGORY_MEMBER_ROLES.DRIVER],
  navigator_allowed: true,
  consent: "",
  route_id: "",
  image: "",
};

export const normalizeCategories = (data) => {
  if (Array.isArray(data)) return data;
  return data?.data || data?.categories || data?.items || [];
};

// export const isJeepRallyAdmin = () => {
//   try {
//     const user = JSON.parse(sessionStorage.getItem("userDetails") || "{}");
//     const role = String(user.role || user.roles || "")
//       .toLowerCase()
//       .trim();
//     return role === "admin" || role === "super admin";
//   } catch {
//     return false;
//   }
// };

export const isJeepRallyAdmin = () => true;

export const toCategoryKey = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
};

export const getCategoryApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  const message = data?.message || fallback;
  if (Array.isArray(data?.errors) && data.errors.length) {
    return `${message}: ${data.errors.join(", ")}`;
  }
  return message;
};

export const filterCategories = (categories, searchTerm) => {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return categories;

  return categories.filter((category) => {
    const title = category?.title?.toLowerCase() || "";
    const key = category?.key?.toLowerCase() || "";
    const description = category?.description?.toLowerCase() || "";
    const routeTitle = getCategoryRouteTitle(category).toLowerCase();
    return (
      title.includes(term) ||
      key.includes(term) ||
      description.includes(term) ||
      routeTitle.includes(term)
    );
  });
};

export const getCategoryRoute = (category) => {
  const route = category?.route_id || category?.route;
  if (!route) return null;
  if (typeof route === "string") return { _id: route };
  return route;
};

export const getCategoryRouteTitle = (category) => {
  const route = getCategoryRoute(category);
  if (!route) return "";
  return route.title || route.name || "";
};

export const getCategoryRouteSummary = (category) => {
  const route = getCategoryRoute(category);
  if (!route || (!route.title && !route.name && route.total_distance_km == null)) {
    return "No route assigned";
  }

  const parts = [route.title || route.name || "Route"];
  if (route.total_distance_km != null && route.total_distance_km !== "") {
    parts.push(`${route.total_distance_km} km`);
  }
  if (route.estimated_time) parts.push(route.estimated_time);
  return parts.join(" · ");
};

export const mapCategoryToForm = (category) => {
  const maxMembers =
    category?.max_members === 0 || category?.max_members
      ? String(Math.max(1, Number(category.max_members) || 1))
      : "1";

  return {
    title: category?.title || "",
    key: category?.key || "",
    description: category?.description || "",
    max_members: maxMembers,
    // Roles are UI-only until the backend supports them.
    member_roles: buildMemberRoles(maxMembers),
    navigator_allowed: category?.navigator_allowed !== false,
    consent: category?.consent || "",
    route_id:
      typeof category?.route_id === "object"
        ? category.route_id?._id || category.route_id?.id || ""
        : category?.route_id || "",
    image: category?.image || "",
  };
};

export const validateCategoryForm = (form, options = {}) => {
  const {
    existingCategories = [],
    currentId = null,
    requireRouteId = false,
  } = options;
  const errors = {};

  if (requireRouteId && !form.route_id?.trim()) {
    errors.route_id = "Route is required.";
  }

  if (!form.title?.trim()) {
    errors.title = "Category title is required.";
  }

  const key = toCategoryKey(form.key);
  if (!key) {
    errors.key = "Category key is required.";
  } else {
    const duplicate = existingCategories.find((category) => {
      const sameKey = toCategoryKey(category?.key) === key;
      const categoryId = category?._id || category?.id;
      return sameKey && categoryId !== currentId;
    });
    if (duplicate) {
      errors.key = "Category key already exists.";
    }
  }

  if (form.max_members === "" || form.max_members === null || form.max_members === undefined) {
    errors.max_members = "max_members is required.";
  } else {
    const maxMembers = Number(form.max_members);
    if (!Number.isInteger(maxMembers)) {
      errors.max_members = "max_members is required.";
    } else if (maxMembers < 1) {
      errors.max_members = "max_members must be at least 1.";
    }
  }

  if (typeof form.navigator_allowed !== "boolean") {
    errors.navigator_allowed = "navigator_allowed is required.";
  }

  return errors;
};

export const isConsentHtmlEmpty = (html) => {
  const text = String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return !text;
};

export const normalizeConsentHtml = (html) => {
  if (isConsentHtmlEmpty(html)) return "";
  return String(html).trim();
};

export const buildCategoryPayload = (form, options = {}) => {
  const { isEdit = false, initialForm = null } = options;

  const title = form.title.trim();
  const key = toCategoryKey(form.key);
  const description = form.description?.trim() || "";
  const consent = normalizeConsentHtml(form.consent);
  const image = form.image?.trim() || "";
  const maxMembers = Number(form.max_members);
  const navigatorAllowed = Boolean(form.navigator_allowed);
  const routeId = form.route_id?.trim() || "";

  if (!isEdit) {
    const payload = {
      title,
      key,
      max_members: maxMembers,
      navigator_allowed: navigatorAllowed,
    };
    if (description) payload.description = description;
    if (consent) payload.consent = consent;
    if (routeId) payload.route_id = routeId;
    if (image) payload.image = image;
    return payload;
  }

  const payload = {};
  const initialTitle = initialForm?.title?.trim() || "";
  const initialKey = toCategoryKey(initialForm?.key);
  const initialDescription = initialForm?.description?.trim() || "";
  const initialConsent = normalizeConsentHtml(initialForm?.consent);
  const initialImage = initialForm?.image?.trim() || "";
  const initialMaxMembers = Number(initialForm?.max_members);
  const initialNavigatorAllowed = Boolean(initialForm?.navigator_allowed);

  if (title !== initialTitle) payload.title = title;
  if (key !== initialKey) payload.key = key;
  if (maxMembers !== initialMaxMembers) payload.max_members = maxMembers;
  if (navigatorAllowed !== initialNavigatorAllowed) {
    payload.navigator_allowed = navigatorAllowed;
  }
  if (description !== initialDescription) payload.description = description;
  if (consent !== initialConsent) payload.consent = consent;
  if (image !== initialImage) payload.image = image;

  return Object.keys(payload).length ? payload : null;
};
